import { api, ApiError } from '../api';
import { localDateKey } from '../dateKey';
import type {
  CircleDetail,
  CircleDashboard,
  CircleInvitePreview,
  CircleLimits,
  CircleListItem,
  CirclePrivacySettings,
  MemberDaySnapshot,
} from '../../types/circles';

export function circleApiErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.body) {
    try {
      const j = JSON.parse(e.body) as { error?: string };
      if (j.error) return j.error;
    } catch {
      /* ignore */
    }
  }
  return e instanceof Error ? e.message : fallback;
}

export async function fetchCircleInvitePreview(inviteCode: string): Promise<CircleInvitePreview> {
  const code = encodeURIComponent(inviteCode.trim().toUpperCase());
  return api<CircleInvitePreview>(`/api/circles/invite/${code}`);
}

export function clientTzOffsetMinutes(): number {
  return new Date().getTimezoneOffset();
}

function circleQuery(dateKey?: string): string {
  const q = new URLSearchParams({ tzOffset: String(clientTzOffsetMinutes()) });
  if (dateKey) q.set('dateKey', dateKey);
  return `?${q.toString()}`;
}

export async function fetchCirclesDashboard(): Promise<CircleDashboard> {
  return api<CircleDashboard>(`/api/circles/dashboard${circleQuery()}`);
}

export async function fetchCircleLimits(): Promise<CircleLimits> {
  return api<CircleLimits>('/api/circles/limits');
}

export async function fetchCircles(): Promise<CircleListItem[]> {
  return api<CircleListItem[]>(`/api/circles${circleQuery()}`);
}

export async function fetchCircleDetail(circleId: string, dateKey?: string): Promise<CircleDetail> {
  return api<CircleDetail>(`/api/circles/${circleId}${circleQuery(dateKey ?? localDateKey())}`);
}

export async function fetchMemberDay(
  circleId: string,
  memberUserId: string,
  dateKey?: string,
): Promise<MemberDaySnapshot> {
  const q = new URLSearchParams({ tzOffset: String(clientTzOffsetMinutes()) });
  if (dateKey) q.set('dateKey', dateKey);
  return api<MemberDaySnapshot>(
    `/api/circles/${circleId}/members/${memberUserId}/day?${q.toString()}`,
  );
}

export async function createCircle(name: string, description?: string): Promise<void> {
  await api('/api/circles', {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
}

export async function joinCircle(inviteCode: string): Promise<void> {
  await api('/api/circles/join', {
    method: 'POST',
    body: JSON.stringify({ inviteCode }),
  });
}

export async function updateCirclePrivacy(circleId: string, settings: Partial<CirclePrivacySettings>): Promise<void> {
  await api(`/api/circles/${circleId}/privacy`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

export async function sendWorkoutPing(
  circleId: string,
  body: { workoutType: string; proposedTime?: string; message?: string; targetUserIds?: string[] },
): Promise<void> {
  await api(`/api/circles/${circleId}/workout-pings`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function respondToPing(
  circleId: string,
  pingId: string,
  response: 'join' | 'maybe' | 'cant',
): Promise<void> {
  await api(`/api/circles/${circleId}/workout-pings/${pingId}/respond`, {
    method: 'POST',
    body: JSON.stringify({ response }),
  });
}

export async function sendNudge(
  circleId: string,
  targetUserId: string,
  type: string,
  message?: string,
): Promise<void> {
  await api(`/api/circles/${circleId}/nudges`, {
    method: 'POST',
    body: JSON.stringify({ targetUserId, type, message }),
  });
}

export async function shareMealToCircle(
  circleId: string,
  meal: {
    title: string;
    calories: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    prepTime?: number;
    rating?: number;
    recipe?: string;
  },
): Promise<void> {
  await api(`/api/circles/${circleId}/meals`, {
    method: 'POST',
    body: JSON.stringify(meal),
  });
}

export async function startChallenge(
  circleId: string,
  body: { title: string; type: string; durationDays: number; goalType: string; target: number },
): Promise<void> {
  await api(`/api/circles/${circleId}/challenges`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function joinChallenge(circleId: string, challengeId: string): Promise<void> {
  await api(`/api/circles/${circleId}/challenges/${challengeId}/join`, { method: 'POST' });
}

export const NUDGE_TEMPLATES: { key: string; label: string; message: string }[] = [
  { key: 'comeback', label: 'Comeback lift?', message: 'We miss you in the Circle. Want to log a quick comeback workout today?' },
  { key: 'spot', label: 'Need a spot?', message: 'Need a spot? I am around if you want to train together.' },
  { key: 'walk', label: 'Walk tonight?', message: 'Walk tonight? A light session keeps momentum alive.' },
  { key: 'protein', label: 'Protein check?', message: 'Protein check — one solid meal can close the gap today.' },
  { key: 'streak', label: 'Keep the streak alive?', message: 'One small session keeps the streak alive.' },
  { key: 'quick20', label: 'Quick 20-minute session?', message: 'Quick 20-minute session? Small wins stack up.' },
];
